import type { SidebarEntry, SidebarGroupDef, SidebarItemDef, SidebarVisibility, SidebarContext } from './types'

/** Returns true if the visibility rules allow the given context. */
export function matchesVisibility(visibility: SidebarVisibility | undefined, context: SidebarContext | undefined): boolean {
  if (!visibility) return true
  if (visibility.requireRoles && visibility.requireRoles.length > 0) {
    const roles = context?.roles ?? []
    if (!visibility.requireRoles.some((r) => roles.includes(r))) return false
  }
  if (visibility.requireOrgType && visibility.requireOrgType.length > 0) {
    const orgType = context?.orgType
    if (!orgType || !visibility.requireOrgType.includes(orgType)) return false
  }
  return true
}

function visibleItems(items: SidebarItemDef[], context: SidebarContext | undefined): SidebarItemDef[] {
  return items
    .filter((item) => matchesVisibility(item.visibility, context))
    .map((item) => (item.children ? { ...item, children: visibleItems(item.children, context) } : item))
}

/** Filter items/sections/groups by visibility rules against the given context. Hides empty groups/sections. */
export function applyVisibility(entries: SidebarEntry[], context: SidebarContext | undefined): SidebarEntry[] {
  return entries.flatMap((entry) => {
    if (!matchesVisibility(entry.visibility, context)) return []
    const items = visibleItems(entry.items, context)
    if (entry.kind === 'section' && entry.groups) {
      const groups = applyVisibility(entry.groups, context) as SidebarGroupDef[]
      if (items.length === 0 && groups.length === 0) return []
      return [{ ...entry, items, groups }]
    }
    if (items.length === 0) return []
    return [{ ...entry, items }] as SidebarEntry[]
  })
}

/** Case-insensitive substring match. */
function matches(label: string, query: string): boolean {
  return label.toLowerCase().includes(query.toLowerCase())
}

/**
 * An item matches on its own label (keeping all its children) or through a
 * child's — then only the matching children stay, under their parent.
 */
function searchItems(items: SidebarItemDef[], q: string): SidebarItemDef[] {
  return items.flatMap((item) => {
    if (matches(item.label, q)) return [item]
    const children = (item.children ?? []).filter((child) => matches(child.label, q))
    return children.length > 0 ? [{ ...item, children }] : []
  })
}

/**
 * Filter by search query.
 *
 * Rules:
 * - Empty query → return entries unchanged.
 * - If a section/group's own label matches → include all its items.
 * - Otherwise, include only items whose label matches. Hide section/group if no items match.
 */
export function applySearch(entries: SidebarEntry[], query: string): SidebarEntry[] {
  const q = query.trim()
  if (!q) return entries

  return entries.flatMap((entry) => {
    const labelMatches = entry.kind === 'group' || (entry.kind === 'section' && entry.label)
      ? matches(entry.label ?? '', q)
      : false

    if (labelMatches) {
      return [entry]
    }
    const items = searchItems(entry.items, q)
    if (entry.kind === 'section' && entry.groups) {
      const groups = applySearch(entry.groups, q) as SidebarGroupDef[]
      if (items.length === 0 && groups.length === 0) return []
      return [{ ...entry, items, groups }]
    }
    if (items.length === 0) return []
    return [{ ...entry, items }] as SidebarEntry[]
  })
}

/** Flattens all leaf items from sections and groups. Useful for command palettes, analytics, etc. */
export function flattenSidebarItems(entries: SidebarEntry[]): SidebarItemDef[] {
  const withChildren = (items: SidebarItemDef[]) => items.flatMap((item) => [item, ...(item.children ?? [])])
  return entries.flatMap((entry) =>
    entry.kind === 'section' && entry.groups
      ? [...withChildren(entry.items), ...flattenSidebarItems(entry.groups)]
      : withChildren(entry.items))
}
